/**
 * SubscriptionContext module.
 * Provides context and state management for subscription, billing, and usage data.
 * Extracted from UserContext for focused re-render boundaries.
 */
// External packages
import { createContext, useContext, useReducer, useEffect, useMemo } from "react";

// Sibling contexts (read-only for derived values).
import { useAuthContext } from "./AuthContext";
import { useAccountContext } from "./AccountContext";

// Relative imports - Utils
import * as logoutChannel from "../shared/utils/events/logoutChannel";

const initialState = {
  subscriptions: [],
  subscriptionDetails: {},
  billingData: {},
  documents: [],
  invoices: [],
  billingUsage: {},
  selectedSubscriptionId: "",
  switchingSubscription: false,
};

function reducer(state, action) {
  switch (action.type) {
    case "STORE_SUBSCRIPTIONS":
      return {
        ...state,
        subscriptions: action.payload,
        selectedSubscriptionId: action.payload?.[0]?.id || "",
      };
    case "STORE_SUB":
      return {
        ...state,
        subscriptionDetails: {
          ...state.subscriptionDetails,
          [action.payload.id]: action.payload,
        },
      };
    case "STORE_BILLING_SINGLE":
      return {
        ...state,
        billingData: { ...state.billingData, [action.id]: action.payload },
      };
    case "STORE_BILLING_BATCH":
      return {
        ...state,
        billingData: { ...state.billingData, ...action.payload },
      };
    case "STORE_DOCUMENTS":
      return { ...state, documents: action.payload };
    case "STORE_INVOICES":
      return { ...state, invoices: action.payload };
    case "STORE_USAGE":
      return {
        ...state,
        billingUsage: {
          ...state.billingUsage,
          [action.payload.id]: action.payload.data,
        },
      };
    case "SELECT_SUB":
      return {
        ...state,
        switchingSubscription: false,
        selectedSubscriptionId: action.payload,
      };
    case "SWITCHING_SUB":
      return { ...state, switchingSubscription: action.payload };
    case "UPDATE_SUBSCRIPTIONS":
      return {
        ...state,
        subscriptions: action.payload,
      };
    case "STORE_SUB_FULL_BATCH":
      return {
        ...state,
        subscriptionDetails: {
          ...state.subscriptionDetails,
          ...action.payload,
        },
      };
    case "REMOVE_SUB": {
      const { [action.id]: _, ...restSubs } = state.subscriptionDetails;
      const { [action.id]: __, ...restBilling } = state.billingData;
      return {
        ...state,
        subscriptionDetails: restSubs,
        billingData: restBilling,
      };
    }
    case "RESET":
      return initialState;
    default:
      return state;
  }
}

const SubscriptionContext = createContext(null);

/**
 * SubscriptionProvider component.
 * Provides subscription, billing, and usage state and actions to its children via context.
 * Must be nested inside AuthProvider and AccountProvider.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element} The provider with subscription context value.
 */
export function SubscriptionProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Read-only from sibling contexts for derived values.
  const { isOwner } = useAuthContext();
  const { account } = useAccountContext();

  // Reset subscription state on logout.
  useEffect(() => {
    return logoutChannel.subscribe(() => dispatch({ type: "RESET" }));
  }, []);

  const storeSubscriptions = (subs) =>
    dispatch({ type: "STORE_SUBSCRIPTIONS", payload: subs });

  const storeSubscriptionData = (p) =>
    dispatch({ type: "STORE_SUB", payload: p });

  const storeBillingSingle = (id, blob) =>
    dispatch({ type: "STORE_BILLING_SINGLE", id, payload: blob });

  const storeBillingBatch = (map) =>
    dispatch({ type: "STORE_BILLING_BATCH", payload: map });

  const selectSubscription = (id) =>
    dispatch({ type: "SELECT_SUB", payload: id });

  const setSwitchingSubscription = (v) =>
    dispatch({ type: "SWITCHING_SUB", payload: v });

  const updateSubscriptions = (subs) =>
    dispatch({ type: "UPDATE_SUBSCRIPTIONS", payload: subs });

  const storeSubscriptionsBulk = (map) =>
    dispatch({ type: "STORE_SUB_FULL_BATCH", payload: map });

  const removeSub = (id) => dispatch({ type: "REMOVE_SUB", id });

  const storeDocuments = (p) =>
    dispatch({ type: "STORE_DOCUMENTS", payload: p });

  const storeInvoices = (p) =>
    dispatch({ type: "STORE_INVOICES", payload: p });

  const storeUsage = (id, data) =>
    dispatch({ type: "STORE_USAGE", payload: { id, data } });

  const value = useMemo(() => {
    const hasServiceCurrent = (serviceName) => {
      const subMeta =
        state.subscriptionDetails[state.selectedSubscriptionId] ??
        state.subscriptions.find(
          (s) => s.id === state.selectedSubscriptionId
        );

      if (!subMeta) return false;
      return (subMeta.services ?? []).some((svc) => svc.name === serviceName);
    };

    const hasDebt =
      isOwner &&
      Number(account.debt) > 0 &&
      account.collection_state_code !== "";

    const notAllowPayDebt = [
      "legal_treatment",
      "legal_treatment_lawyer",
    ].includes(account.collection_state_code);

    return {
      subscriptions: state.subscriptions,
      subscriptionDetails: state.subscriptionDetails,
      billingData: state.billingData,
      documents: state.documents,
      invoices: state.invoices,
      billingUsage: state.billingUsage,
      selectedSubscriptionId: state.selectedSubscriptionId,
      switchingSubscription: state.switchingSubscription,
      currentSubscription:
        state.subscriptionDetails[state.selectedSubscriptionId] || null,

      hasDebt,
      notAllowPayDebt,
      hasServiceCurrent,

      // Actions.
      storeSubscriptions,
      storeSubscriptionData,
      storeBillingSingle,
      storeBillingBatch,
      selectSubscription,
      setSwitchingSubscription,
      updateSubscriptions,
      storeSubscriptionsBulk,
      removeSub,
      storeDocuments,
      storeInvoices,
      storeUsage,
    };
  }, [state, isOwner, account.debt, account.collection_state_code]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

/**
 * Custom hook to access the SubscriptionContext value.
 *
 * @returns {Object} Subscription context value.
 */
export function useSubscriptionContext() {
  const context = useContext(SubscriptionContext);
  if (context === undefined || context === null) {
    throw new Error(
      "useSubscriptionContext must be used within a SubscriptionProvider"
    );
  }
  return context;
}
